<template>
    <skeleton v-if="plan === undefined" />
    <not-found v-else-if="plan === null" />
    <purchase-agreement
        v-else
        :plan="plan"
    />
</template>

<script setup lang="ts">
import { computed, onServerPrefetch } from 'vue'
import {
    mapLanguageToLocale,
    type SupportedLocale
} from '~/composables/create-language-context'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import {
    type PurchaseAgreementInput,
    usePurchaseAgreement
} from '~/components/PurchaseContract/Agreement/use-purchase-agreement'
import PurchaseAgreement from '~/components/PurchaseContract/Agreement/PurchaseAgreement.vue'
import Skeleton from 'primevue/skeleton'
import NotFound from '~/components/NotFound/NotFound.vue'
import { useScopedGlobalI18n } from '~/composables/use-scoped-global-i18n'

const props = defineProps<{
    purchaseAgreementInput: PurchaseAgreementInput | null | undefined
}>()

const rootSelectedLanguage = useSelectedLanguage()

const purchaseAgreementInput = computed(() => {
    return props.purchaseAgreementInput
})
const purchaseAgreementLanguage = computed(() => {
    // Null plan language means the agreement should follow the customer's
    // selected storefront language instead of forcing a separate locale.
    return props.purchaseAgreementInput?.language ?? rootSelectedLanguage.value
})

const purchaseAgreementLocale = computed<SupportedLocale>(() => {
    return mapLanguageToLocale(purchaseAgreementLanguage.value)
})

useScopedGlobalI18n(purchaseAgreementLocale)

const { plan, suspense } = usePurchaseAgreement(purchaseAgreementInput)

onServerPrefetch(async () => {
    // The parent prefetches the lightweight agreement input; this component then
    // prefetches the translated agreement under the scoped language boundary.
    await suspense()
})
</script>
